import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ETIQUETA_APARATO_SISTEMA, fechaLocalIso, type AparatoSistema, type TipoControl } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';

interface TrabajadorPortal {
  id: string;
  dni: string;
  nombre: string;
  legajo: string | null;
  email: string | null;
  domicilio: string | null;
  sexo: string | null;
  edad: number | null;
}

interface AusenciaRegistro {
  id: string;
  tipoControl: TipoControl;
  fechaInicio: string;
  domicilio: string;
  estado: string; // 'REGISTRADA' | 'CONTROL_SOLICITADO' | 'CONTROL_EN_CURSO' | 'COMPLETADA'
  ordenId: string | null;
  observaciones: string | null;
  trabajador: {
    nombre: string;
    dni: string;
    legajo: string | null;
  };
  constancia: {
    codigo: string;
    estado: string;
    resultado: string;
    aptitud: string | null;
    fechaReinsercion: string | null;
    aparatoSistema: string | null;
    fechaControl: string | null;
    registradoEn: string | null;
  } | null;
}

const ESTADO_TXT: Record<string, string> = {
  REGISTRADA: 'Registrada',
  CONTROL_SOLICITADO: 'Control Solicitado',
  CONTROL_EN_CURSO: 'Control en Curso',
  COMPLETADA: 'Completada',
};

const ESTADO_INSIGNIA: Record<string, string> = {
  REGISTRADA: 'gris',
  CONTROL_SOLICITADO: 'ambar',
  CONTROL_EN_CURSO: 'azul',
  COMPLETADA: 'verde',
};

const RESULTADO_TXT: Record<string, string> = {
  REALIZADA: 'Sí',
  PACIENTE_AUSENTE: 'No (Ausente)',
  RECHAZA_CONTROL: 'No (Rechazó)',
  DOMICILIO_ERRONEO: 'No (Dom. Erróneo)',
};

function obtenerHora(isoString?: string | null): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '—';
  }
}

function obtenerFecha(isoString?: string | null): string {
  if (!isoString) return '—';
  try {
    if (isoString.includes('-') && !isoString.includes('T')) {
      const [y, m, d] = isoString.split('-');
      return `${d}/${m}/${y}`;
    }
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

export function BajasPortal() {
  const [ausencias, setAusencias] = useState<AusenciaRegistro[] | null>(null);
  const [error, setError] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);

  // Formulario
  const [dni, setDni] = useState('');
  const [nombre, setNombre] = useState('');
  const [legajo, setLegajo] = useState('');
  const [email, setEmail] = useState('');
  const [domicilio, setDomicilio] = useState('');
  const [tipoControl, setTipoControl] = useState<TipoControl>('');
  // Fecha local, no UTC: toISOString() adelanta un día entre las 21:00 y las
  // 00:00 hora Argentina, proponiendo mañana como día de control.
  const [fechaInicio, setFechaInicio] = useState(() => fechaLocalIso());
  const [observaciones, setObservaciones] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [exitoMsg, setExitoMsg] = useState('');

  // Selector de Empleados
  const [empleados, setEmpleados] = useState<TrabajadorPortal[]>([]);
  const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
  const [busquedaEmpleado, setBusquedaEmpleado] = useState('');
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState<TrabajadorPortal | null>(null);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);

  // Tramos de control (tipos de visita) con su tarifa vigente — el Operador
  // los administra desde su panel; el formulario ya no asume 3 valores fijos.
  const [tramos, setTramos] = useState<{ tipoControl: TipoControl; tipoControlNombre: string; montoCentavos: number | null }[]>([]);

  useEffect(() => {
    api.get<{ tipoControl: TipoControl; tipoControlNombre: string; montoCentavos: number | null }[]>('/api/portal/tarifas')
      .then((res) => {
        setTramos(res);
        setTipoControl((actual) => actual || res[0]?.tipoControl || '');
      })
      .catch(() => {}); // el formulario sigue siendo usable sin el precio informativo
  }, []);

  function textoTarifa(tipo: TipoControl): string {
    const centavos = tramos.find((t) => t.tipoControl === tipo)?.montoCentavos;
    if (centavos == null) return 'consultá tarifa vigente';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(centavos / 100);
  }

  function nombreTipoControl(tipo: TipoControl): string {
    return tramos.find((t) => t.tipoControl === tipo)?.tipoControlNombre ?? tipo;
  }

  // Modo de edición
  const [modoEdicion, setModoEdicion] = useState(false);
  const [ausenciaIdEditando, setAusenciaIdEditando] = useState<string | null>(null);

  useEffect(() => {
    if (modalAbierto) {
      setCargandoEmpleados(true);
      api.get<TrabajadorPortal[]>('/api/portal/trabajadores')
        .then((res) => {
          setEmpleados(res || []);
        })
        .catch((err) => {
          console.error('Error al cargar empleados:', err);
        })
        .finally(() => {
          setCargandoEmpleados(false);
        });
    } else {
      setMostrarSugerencias(false);
    }
  }, [modalAbierto]);

  const sugerencias = useMemo(() => {
    const q = busquedaEmpleado.toLowerCase().trim();
    if (!q) return [];
    return empleados.filter(
      (e) =>
        e.nombre.toLowerCase().includes(q) ||
        e.dni.includes(q) ||
        (e.legajo && e.legajo.toLowerCase().includes(q))
    );
  }, [empleados, busquedaEmpleado]);

  async function cargarAusencias() {
    try {
      setAusencias(await api.get<AusenciaRegistro[]>('/api/portal/ausencias'));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las ausencias');
    }
  }

  useEffect(() => {
    void cargarAusencias();
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (modoEdicion) {
      if (!domicilio || !fechaInicio) {
        setErrorForm('Por favor completa todos los campos obligatorios (*).');
        return;
      }
      setRegistrando(true);
      setErrorForm('');
      setExitoMsg('');
      try {
        await api.put(`/api/portal/ausencias/${ausenciaIdEditando}`, {
          domicilio,
          tipoControl,
          fechaInicio,
          observaciones,
        });
        setExitoMsg('Control actualizado correctamente.');
        await cargarAusencias();
        setTimeout(() => {
          cerrarModalLimpiando();
        }, 1500);
      } catch (err) {
        setErrorForm(err instanceof ErrorApi ? err.message : 'Error al actualizar el control');
      } finally {
        setRegistrando(false);
      }
    } else {
      if (!dni || !nombre || !domicilio || !fechaInicio) {
        setErrorForm('Por favor completa todos los campos obligatorios (*).');
        return;
      }
      setRegistrando(true);
      setErrorForm('');
      setExitoMsg('');
      try {
        await api.post('/api/portal/ausencias', {
          dni,
          nombre,
          legajo: legajo || undefined,
          email: email || undefined,
          domicilio,
          tipoControl,
          fechaInicio,
          observaciones: observaciones || undefined,
        });
        setExitoMsg('Ausencia registrada correctamente.');
        await cargarAusencias();
        setTimeout(() => {
          cerrarModalLimpiando();
        }, 1500);
      } catch (err) {
        setErrorForm(err instanceof ErrorApi ? err.message : 'Error al registrar la ausencia');
      } finally {
        setRegistrando(false);
      }
    }
  }

  function abrirEditar(ausencia: AusenciaRegistro) {
    setModoEdicion(true);
    setAusenciaIdEditando(ausencia.id);
    
    setDni(ausencia.trabajador.dni);
    setNombre(ausencia.trabajador.nombre);
    setLegajo(ausencia.trabajador.legajo || '');
    setConsolidatedFields('');
    setDomicilio(ausencia.domicilio);
    setTipoControl(ausencia.tipoControl);
    setFechaInicio(ausencia.fechaInicio);
    setObservaciones(ausencia.observaciones || '');
    
    setEmpleadoSeleccionado({
      id: '',
      dni: ausencia.trabajador.dni,
      nombre: ausencia.trabajador.nombre,
      legajo: ausencia.trabajador.legajo,
      email: '',
      domicilio: ausencia.domicilio,
      sexo: null,
      edad: null,
    });
    setBusquedaEmpleado(`${ausencia.trabajador.nombre} (DNI ${ausencia.trabajador.dni})`);
    setModalAbierto(true);
  }

  function abrirCrear() {
    setModoEdicion(false);
    setAusenciaIdEditando(null);
    cerrarModalLimpiando();
    setModalAbierto(true);
  }

  async function eliminar(id: string) {
    if (!window.confirm('¿Estás seguro de que deseas cancelar/eliminar esta solicitud de control médico?')) {
      return;
    }
    try {
      await api.delete(`/api/portal/ausencias/${id}`);
      await cargarAusencias();
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al cancelar la solicitud');
    }
  }


  function setConsolidatedFields(val: string) {
    setEmail(val);
  }

  function intentarCerrar() {
    const isDirty = dni !== '' || nombre !== '' || legajo !== '' || email !== '' || domicilio !== '' || observaciones !== '';
    if (isDirty) {
      if (window.confirm('¿Estás seguro de que deseas salir? Los datos no guardados se perderán.')) {
        cerrarModalLimpiando();
      }
    } else {
      cerrarModalLimpiando();
    }
  }

  function cerrarModalLimpiando() {
    setModalAbierto(false);
    setErrorForm('');
    setExitoMsg('');
    setDni('');
    setNombre('');
    setLegajo('');
    setConsolidatedFields('');
    setDomicilio('');
    setTipoControl(tramos[0]?.tipoControl || '');
    setEmpleadoSeleccionado(null);
    setBusquedaEmpleado('');
    setMostrarSugerencias(false);
    setModoEdicion(false);
    setAusenciaIdEditando(null);
    setObservaciones('');
    setFechaInicio(fechaLocalIso());
  }

  return (
    <>
      <div className="fila" style={{ marginBottom: 20, alignItems: 'center' }}>
        <div>
          <h2>Ausencias por Enfermedad y Solicitudes de Auditoría</h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Registra las ausencias de los trabajadores con reposo médico para solicitar su control médico laboral (art. 210 LCT).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="primario" style={{ width: 'auto' }} onClick={abrirCrear}>
            + Registrar Nueva Ausencia
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}
      {ausencias === null && <div className="cargando">Cargando historial de ausencias…</div>}
      {ausencias && ausencias.length === 0 && <p className="muted">No se registran casos de ausencias activas.</p>}

      {ausencias && ausencias.length > 0 && (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Trabajador</th>
                <th>Día de Control</th>
                <th>Hora</th>
                <th>Domicilio de Visita</th>
                <th>Tipo de Control</th>
                <th>Estado</th>
                <th>¿Se realiza?</th>
                <th>¿Incapacita?</th>
                <th>Fecha de Retorno</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ausencias.map((b) => (
                <tr key={b.id}>
                  <td>
                    <strong>{b.trabajador.nombre}</strong>
                    <div className="mini">DNI {b.trabajador.dni} {b.trabajador.legajo ? `· Legajo ${b.trabajador.legajo}` : ''}</div>
                  </td>
                  <td>{b.constancia?.fechaControl ? obtenerFecha(b.constancia.fechaControl) : '—'}</td>
                  <td>{b.constancia?.registradoEn ? obtenerHora(b.constancia.registradoEn) : '—'}</td>
                  <td>{b.domicilio}</td>
                  <td>{nombreTipoControl(b.tipoControl)}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                      <span className={`insignia ${ESTADO_INSIGNIA[b.estado] ?? 'gris'}`}>
                        {ESTADO_TXT[b.estado] ?? b.estado}
                      </span>
                    </div>
                  </td>
                  <td>
                    {b.constancia?.resultado ? (
                      <span className={`insignia ${b.constancia.resultado === 'REALIZADA' ? 'verde' : 'roja'}`}>
                        {RESULTADO_TXT[b.constancia.resultado] ?? b.constancia.resultado}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {b.constancia ? (
                      b.constancia.aptitud === 'APTO_PARA_TRABAJAR' ? (
                        <span className="insignia verde">No (Apto)</span>
                      ) : (
                        <span className="insignia ambar">
                          Sí{b.constancia.aparatoSistema ? ` (${ETIQUETA_APARATO_SISTEMA[b.constancia.aparatoSistema as AparatoSistema] ?? b.constancia.aparatoSistema})` : ''}
                        </span>
                      )
                    ) : '—'}
                  </td>
                  <td>
                    {b.constancia?.aptitud === 'NO_APTO_TEMPORAL' && b.constancia.fechaReinsercion
                      ? obtenerFecha(b.constancia.fechaReinsercion)
                      : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      {b.estado !== 'COMPLETADA' ? (
                        <>
                          <button
                            type="button"
                            className="secundario"
                            style={{ padding: '6px 12px', fontSize: 12, borderRadius: 'var(--r-sm)', minWidth: 'auto', height: 'auto' }}
                            onClick={() => abrirEditar(b)}
                            title="Editar control"
                          >
                            ✏️ Editar
                          </button>
                          <button
                            type="button"
                            className="peligro"
                            style={{ padding: '6px 12px', fontSize: 12, borderRadius: 'var(--r-sm)', minWidth: 'auto', height: 'auto' }}
                            onClick={() => eliminar(b.id)}
                            title="Cancelar/Eliminar control"
                          >
                            ✕ Cancelar
                          </button>
                        </>
                      ) : (
                        <span className="insignia verde" style={{ opacity: 0.8 }} title="El control ya fue realizado y no se puede editar">🔒 Realizado</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal / Formulario de Registro */}
      {modalAbierto && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) intentarCerrar(); }}>
          <div className="dialogo glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{modoEdicion ? 'Editar Control Solicitado' : 'Registrar Ausencia de Trabajador'}</h3>
              <button
                type="button"
                className="fantasma"
                onClick={intentarCerrar}
                style={{ fontSize: 20, padding: 4, lineHeight: 1 }}
                aria-label="Cerrar modal"
              >
                ✕
              </button>
            </div>
            
            {errorForm && <div className="error-box">{errorForm}</div>}
            {exitoMsg && <div className="insignia verde" style={{ width: '100%', padding: '10px 12px', boxSizing: 'border-box', marginBottom: 12, textAlign: 'center', fontSize: 13 }}>{exitoMsg}</div>}

            <form onSubmit={guardar}>
              {/* Buscador de Empleado */}
              <div className="campo" style={{ marginBottom: 16, position: 'relative' }}>
                <label htmlFor="buscar-empleado">Buscar Empleado (*)</label>
                <input
                  id="buscar-empleado"
                  type="text"
                  placeholder="Escribí nombre, DNI o legajo del empleado..."
                  value={busquedaEmpleado}
                  disabled={modoEdicion}
                  onChange={(e) => {
                    setBusquedaEmpleado(e.target.value);
                    setMostrarSugerencias(true);
                    if (empleadoSeleccionado) {
                      setEmpleadoSeleccionado(null);
                      setDni('');
                      setNombre('');
                      setLegajo('');
                      setEmail('');
                      setDomicilio('');
                    }
                  }}
                  onFocus={() => setMostrarSugerencias(true)}
                  required={!empleadoSeleccionado}
                />
                
                {cargandoEmpleados && <div className="mini" style={{ marginTop: 4, color: 'var(--verde)' }}>Cargando personal...</div>}
                
                {mostrarSugerencias && busquedaEmpleado.trim().length > 0 && !empleadoSeleccionado && (
                  <div
                    className="glass"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 100,
                      maxHeight: 200,
                      overflowY: 'auto',
                      border: '1px solid var(--borde)',
                      borderRadius: 6,
                      background: 'var(--panel)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      marginTop: 4,
                    }}
                  >
                    {sugerencias.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 13, color: 'var(--texto-2)' }}>
                        No se encontraron empleados. Cargalos primero en la pestaña "Nómina de Empleados".
                      </div>
                    ) : (
                      sugerencias.map((emp) => (
                        <div
                          key={emp.id}
                          className="opcion-sug"
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--borde-suave, #f1f5f9)',
                            fontSize: 13,
                          }}
                          onClick={() => {
                            setEmpleadoSeleccionado(emp);
                            setBusquedaEmpleado(`${emp.nombre} (DNI ${emp.dni})`);
                            setDni(emp.dni);
                            setNombre(emp.nombre);
                            setLegajo(emp.legajo || '');
                            setEmail(emp.email || '');
                            setDomicilio(emp.domicilio || '');
                            setMostrarSugerencias(false);
                          }}
                        >
                          <strong>{emp.nombre}</strong>
                          <div className="mini" style={{ color: 'var(--texto-2)', marginTop: 2 }}>
                            DNI: {emp.dni} {emp.legajo ? `· Legajo: ${emp.legajo}` : ''}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Confirmación de Selección */}
              {empleadoSeleccionado && (
                <div
                  className="glass"
                  style={{
                    padding: 12,
                    borderRadius: 6,
                    border: '1px solid var(--borde)',
                    background: 'rgba(16, 185, 129, 0.05)',
                    marginBottom: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#10b981', fontWeight: 'bold' }}>
                    ✓ Empleado Seleccionado
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 'bold' }}>{empleadoSeleccionado.nombre}</div>
                  <div className="mini" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span><strong>DNI:</strong> {empleadoSeleccionado.dni}</span>
                    {empleadoSeleccionado.legajo && <span><strong>Legajo:</strong> {empleadoSeleccionado.legajo}</span>}
                    {empleadoSeleccionado.email && <span><strong>Email:</strong> {empleadoSeleccionado.email}</span>}
                  </div>
                </div>
              )}

              {!empleadoSeleccionado && (
                <div style={{ marginBottom: 16, fontSize: 13, background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.15)', padding: 12, borderRadius: 6, color: 'var(--texto-2)', lineHeight: 1.4 }}>
                  💡 Para registrar la ausencia, buscá y seleccioná un empleado cargado en el sistema utilizando el buscador superior.
                </div>
              )}

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="domicilio">Domicilio de Visita (*)</label>
                <input
                  id="domicilio"
                  type="text"
                  required
                  disabled={!empleadoSeleccionado}
                  value={domicilio}
                  onChange={(e) => setDomicilio(e.target.value)}
                  placeholder="Ej. Av. España 1240, San Luis"
                />
                <div className="campo-ayuda">Dirección donde se efectuará el control</div>
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="tipoControl">Tipo de Visita (*)</label>
                <select
                  id="tipoControl"
                  disabled={!empleadoSeleccionado}
                  value={tipoControl}
                  onChange={(e) => setTipoControl(e.target.value as TipoControl)}
                >
                  {tramos.map((t) => (
                    <option key={t.tipoControl} value={t.tipoControl}>
                      {t.tipoControlNombre} — {textoTarifa(t.tipoControl)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="campo" style={{ marginBottom: 20 }}>
                <label htmlFor="fechaInicio">Día de Control (*)</label>
                <input
                  id="fechaInicio"
                  type="date"
                  required
                  disabled={!empleadoSeleccionado}
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>

              <div className="campo" style={{ marginBottom: 20 }}>
                <label htmlFor="observaciones">Observaciones de la Visita (opcional)</label>
                <textarea
                  id="observaciones"
                  disabled={!empleadoSeleccionado}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Ej. Tocar timbre 3B, entrar por pasillo, etc."
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="secundario" onClick={intentarCerrar} disabled={registrando}>
                  Cancelar
                </button>
                <button type="submit" className="primario" disabled={registrando}>
                  {registrando ? 'Guardando…' : modoEdicion ? 'Guardar Cambios' : 'Registrar Ausencia'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
